/**
 * English UI catalogue for namespace 'import' (W2-09 publish-import): opening and importing charts, the import queue
 * banner and summary, the import report dialog, the skin install dialog, the batch-export picker, the merge into
 * the open project, the `file.import*` commands, their settings row and help topic.
 */

import { catalogOf, defineMessages } from '../../i18n/define';

export const m = defineMessages('import', {
  // ---- commands ----
  cmdImport: 'Open a chart file...',
  cmdImportFolder: 'Open a chart folder...',
  cmdImportSummary: 'Show the last import report',
  cmdCancelImport: 'Cancel the remaining imports',
  reasonNoSummary: 'Nothing has been imported in this session yet.',
  reasonNotRunning: 'No import is running.',

  // ---- queue and banner ----
  bannerRunning: 'Importing {index} of {total}: {name}',
  bannerRunningOne: 'Importing {name}',
  bannerWaiting: 'Waiting for your choice: {name}',
  progressLabel: 'Import progress',
  cancel: 'Cancel',
  cancelImports: 'Cancel the remaining imports',
  bannerDone:
    'Import finished: {created, plural, =0 {no new projects} one {# new project} other {# new projects}}{failed, plural, =0 {} one {, # file not imported} other {, # files not imported}}.',
  viewReport: 'View report',
  dismiss: 'Dismiss',
  toastQueued: '{count, plural, one {# more chart was} other {# more charts were}} added to the imports.',
  toastCancelled: '{count, plural, one {# import was} other {# imports were}} cancelled.',
  toastNothing: 'There is nothing to import in these files.',
  toastSkipped:
    '{count, plural, one {# file was} other {# files were}} left out: {count, plural, one {it is} other {they are}} not inside a chart folder.',
  toastCreated: 'Imported {name} as a new project.',
  toastFailed: '{name} could not be imported: {error}',
  toastFailedMany: '{count, plural, one {# file} other {# files}} could not be imported: {names}.',
  errorUnexpected: 'The import stopped unexpectedly: {error}',
  createFailed: 'The project could not be saved: {error}',
  openFailed: 'The imported project is in the Library but could not be opened: {error}',
  unknownError: 'unknown error',

  // ---- report dialog ----
  /** Before anything is created: the dialog decides (UX-081). */
  reportTitle: 'Ready to open "{name}"',
  /** First line of the copied / saved report. */
  reportTextTitle: 'Import report of "{name}"',
  alsoRead: 'Also read: {list}',
  featEffects:
    'extra.json {count, plural, one {# effect} other {# effects}}{custom, plural, =0 {} one { (# custom shader)} other { (# custom shaders)}}',
  featVideos: '{count, plural, one {# video} other {# videos}}',
  featTempo: '{count, plural, one {# tempo change} other {# tempo changes}}',
  featTexture: '{count, plural, one {# image line} other {# image lines}}',
  featGif: '{count, plural, one {# GIF line} other {# GIF lines}}',
  featText: '{count, plural, one {# text line} other {# text lines}}',
  featPaint: '{count, plural, one {# paint line} other {# paint lines}}',
  featUi: '{count, plural, one {# UI line} other {# UI lines}}',
  featParents: '{count, plural, one {# parent link} other {# parent links}}',
  featControls: '{count, plural, one {# line with note controls} other {# lines with note controls}}',
  formatRpe: 'RPE',
  formatPgr1: 'Phigros official, format 1',
  formatPgr3: 'Phigros official, format 3',
  formatPec: 'PhiEditer (PEC)',
  rpeVersionPart: 'RPEVersion {version}',
  linesPart: '{count, plural, one {# line} other {# lines}}',
  notesPart: '{count, plural, one {# note} other {# notes}}',
  eventsPart: '{count, plural, one {# event} other {# events}}',
  manifestPart: 'manifest: {source, select, yml {info.yml} txt {info.txt} csv {info.csv} other {taken from the chart}}',
  phiraLoads: 'Phira would load this file: yes',
  phiraRefuses:
    'Phira would load this file: no. The editor repaired what Phira refuses or crashes on (see Changed); export the chart again before sharing it.',
  exactSummary:
    'Loaded exactly: {events, plural, one {# event} other {# events}}, {notes, plural, one {# note} other {# notes}}, {files, plural, =0 {no files} one {# file} other {# files}}{missing, plural, =0 {} one { (# missing)} other { (# missing)}}.',
  classExact: 'Loaded exactly',
  classNormalized: 'Normalised (renders the same in Phira)',
  classChanged: 'Changed (Phira crashed, refused it, or depended on playback history)',
  classFlagged: 'Kept but flagged',
  classRpeOnly: 'Not used by Phira, kept for RPE',
  entryTimes: '{count, plural, one {# time} other {# times}}',
  entryLine: 'line {line}',
  moreEntries: '{count, plural, one {# more entry} other {# more entries}}: copy or save the report to see all.',
  phiraWarnings: 'Phira shows these warnings when it imports the file:',
  openAs: 'Open as',
  openNew: 'New project',
  openNewHelp: 'Creates a new project. Nothing that already exists is changed.',
  openMerge: 'Add into the current project',
  openMergeHelp: 'Its lines, notes, groups and the files they use are added at the beat below.',
  mergeBeat: 'At beat',
  mergeAtPlayhead: 'Playhead',
  mergeAlign: 'Placement',
  alignAuto: 'Automatic (by beats when the tempos match, otherwise by song time)',
  alignBeats: 'By beats (notes keep their beat grid)',
  alignMusic: 'By song time (tempo and offset of both charts applied)',
  showInProblems: 'Open and show in Problems',
  copyReport: 'Copy report',
  saveReport: 'Save report',
  reportCopied: 'Report copied.',
  copyFailed: 'The browser did not allow copying to the clipboard.',
  open: 'Open',
  add: 'Add',
  reportFileName: '{name} import report.txt',

  // ---- merge ----
  txnMerge: 'Add the lines of {name}',
  toastMerged: 'Added {lines, plural, one {# line} other {# lines}} from {name}.',
  mergeNoProject: 'Open a project to add the chart into it.',
  versionBeforeImport: 'Before importing {name}',

  // ---- skin install ----
  skinTitle: 'Install the skin "{name}"?',
  skinTitleInvalid: 'This resource pack cannot be installed',
  skinBy: 'by {author}',
  skinHitFx: 'Hit effect: {cols} x {rows} frames, {duration} s',
  skinSounds: 'Own sounds: {list}',
  skinNoSounds: 'Uses the default hit sounds.',
  skinImages: 'Images of the skin',
  skinErrors: 'Phira would refuse this pack:',
  skinWarnings: 'Notes:',
  skinUseNow: 'Use it in the preview now',
  skinInstall: 'Install',
  skinInstalled: 'Skin "{name}" installed.',
  skinInstallFailed: 'The skin could not be installed.',
  imageAlt: '{name} image of the skin',

  // ---- batch picker ----
  batchTitle: 'Several charts in one file',
  batchBody:
    'This file holds {count, plural, one {# chart} other {# charts}} (a Phira batch export, or a Phiedit backup of all your charts). Choose which to import; each becomes its own project.',
  batchSelectAll: 'Select all',
  batchList: 'Charts in the file',
  batchImport: 'Import {count, plural, =0 {charts} one {# chart} other {# charts}}',
  batchNone: 'Choose at least one chart.',
  sizeB: '{size} B',
  sizeKB: '{size} kB',
  sizeMB: '{size} MB',
  sizeGB: '{size} GB',
  plainName: '{name}',

  // ---- summary ----
  summaryTitle: 'Import report',
  summaryIntro: '{count, plural, one {# file} other {# files}} in this import.',
  outcomeProject: 'New project: {title}',
  outcomeMerged: 'Added {lines, plural, one {# line} other {# lines}} to the open chart from {name}',
  outcomeSkin: 'Skin installed: {name}',
  outcomeSkinSkipped: 'Skin not installed: {name}',
  outcomeBatch: 'Batch export: {picked} of {total} charts chosen',
  outcomeMedia: 'Only media files ({count, plural, one {# file} other {# files}}): no chart was found.',
  outcomeMediaStarted: 'Media files given to the new chart wizard',
  startChart: 'Start a new chart with them',
  outcomeCancelled: 'Not imported (cancelled)',
  outcomeFailed: 'Not imported: {error}',
  classCounts: '{changed} changed · {normalized} normalised · {flagged} flagged',
  outcomeList: 'Imported files',
  openProject: 'Open',
  showReport: 'Report',
  importMore: 'Import more',
  dropHere: 'Drop .pez or .zip packages, chart files or whole folders anywhere, or choose them here.',
  chooseFiles: 'Choose files...',
  chooseFolder: 'Choose a folder...',
  close: 'Close',

  // ---- settings ----
  settingAlign: 'Placement when adding an imported chart to the open one',

  // ---- help ----
  helpTitle: 'Opening and importing charts',
  helpBody:
    'Drop a .pez or .zip package, an RPE chart (.json), an official Phigros chart, a PhiEditer chart (.pec) or a whole folder anywhere, or press Ctrl+O. Several files or folders are imported one after the other, each as its own project, with one report at the end.\n\nThe import report says what loaded exactly, what was normalised (Phira shows it the same), what was changed because Phira would crash or refuse it, and what is kept only for RPE. Imports never overwrite a project: choose a new project, or add the chart into the open one at a beat.\n\nA Phira resource pack installs as a skin; a song or picture on its own starts the new chart wizard.',
});

export default catalogOf(m);
